// app/utils/global.js
import Cookies from 'js-cookie';
import axios from 'axios';
import { useRouter } from 'next/navigation';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

// 定数
export const COOKIE_DOMAIN_TITLE = 'ドメイン名称';
export const COOKIE_AUTH_TOKEN = '認証TOKEN';
export const COOKIE_AUTH_TOKEN_EXPIRES_AT = '認証TOKEN有効期限';
export const COOKIE_EMPLOYEE_DOMAIN = '社員DOMAIN';
export const COOKIE_EMPLOYEE_NAME = '社員名称';
export const COOKIE_EMPLOYEE_FLAG = '社員フラグ';
export const SESSION_MISMATCH_EVENT = "oknet-session-mismatch";

// AI音声合成などもあり、デフォルト3分とする
export const DEFAULT_AXIOS_TIMEOUT_MS = 3 * 360 * 1000;
axios.defaults.timeout = DEFAULT_AXIOS_TIMEOUT_MS;
// ワーカー系はデフォルト30分（一括文字起こしなどは15分程度かかる場合がある）
export const WORKER_PUSH_TIMEOUT_MS = 30 * 60 * 1000;

// スプレッドキー
export enum FormPkeyState {
  NONE = "<none>",    // → リスト表示モード（何も選択していない状態）
  NEW = "<new>",      // → 新規登録モード
}

// スプレッド参照権限
export enum MetaUserAllow {
  NONE = "NONE",    // → 表示権限なし
  R = "R",          // → 読み取り可能
  RW = "RW",        // → 読み書き可能
}

// スプレッドフォーム表示設定パラメータ
export enum FormCursorType {
  ALL_CONFIRM = "all_confirm",              // 全表示・確認してから編集（デフォルト）
  ALL_CONFIRM_SKIP = "all_direct",          // 全表示・確認無し即編集
  STEP_FORWARD_ONLY = "step_forward_only",  // 1問ずつ・戻れない・確認無し即編集
  STEP_FREE = "step_free",                  // 1問ずつ・戻れる・確認無し即編集
}

// 出力タイプ
export enum OutputType {
  SCREEN = "screen",    // 検索画面表示
  CSV = "csv",          // 検索CSV出力
}

// バイナリ表示タイプ
export enum BinaryType {
  LOW = "low",     // 一覧用(48px)
  MID = "mid",     // 中サイズ(72px)
  HIGH = "high",   // 詳細サイズ(144px)
}

export const getApiBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    // ブラウザ側
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    return isLocal ? "http://127.0.0.1:8300/api" : `${window.location.origin}/api`;
  }
  // SSR/ビルド時
  return "https://oknetse.kmgr.jp/api";
};

// export const systemTitle =
//   typeof window !== 'undefined' && window.location.hostname === 'oknetse.kmgr.jp'
//     ? "OKNET-SE"
//     : "OKNET-SE DEVELOP";
export const systemTitle = "OKNET-SE"

export function getTitle(text: string, maxBytes: number) {
    let bytes = 0;
    let result = '';
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        // 改行でカット
        if (c === '\r' || c === '\n') {
            return result;
        }
        // SJIS風バイト判定（半角英数記号→1バイト、それ以外→2バイト）
        const code = c.charCodeAt(0);
        const charBytes = ((code >= 0 && code <= 127) || (code >= 65377 && code <= 65439)) ? 1 : 2;
        if (bytes + charBytes > maxBytes) {
            // カット発生時は必ず「...」を付与
            return result + '...';
        }
        bytes += charBytes;
        result += c;
    }
    return result;
}

export interface BaseInterface {
    user_guid: string;
    user_domain: string;
    ip_address: string;
    user_agent: string;
}

export interface BaseTokenInterface {
    token: string;
    user_domain: string;
    ip_address: string;
    user_agent: string;
}

export function notifySessionMismatch() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SESSION_MISMATCH_EVENT));
}

export function getCurrentAuthToken(authToken: string | null | undefined): string | undefined {
  return Cookies.get(COOKIE_AUTH_TOKEN) ?? authToken ?? undefined;
}

export function createBaseTokenInterface(authToken: string | null | undefined, userDomain: string | null | undefined): BaseTokenInterface {
  const currentToken = getCurrentAuthToken(authToken);
  const currentDomain = Cookies.get(COOKIE_EMPLOYEE_DOMAIN) ?? userDomain;
  if (!currentToken || !userDomain) {
    throw new Error("認証トークンが取得できません");
  }
  if (currentDomain && currentDomain !== userDomain) {
    notifySessionMismatch();
    throw new Error("セッションが別ドメインに切り替わりました");
  }
  return {
    token: currentToken,
    user_domain: userDomain,
    ip_address: window.location.hostname,
    user_agent: navigator.userAgent,
  };
}

export function updateAuthTokenCookie(authToken: any) {
  if (typeof window === "undefined") return;
  const token = authToken?.token;
  const tokenExpiresAt = authToken?.token_expires_at;
  const tokenCookieExpiresAt = authToken?.token_reissue_expires_at;
  if (!token || !tokenExpiresAt) return;
  const cookieOptions = { expires: new Date(tokenCookieExpiresAt || tokenExpiresAt) };
  Cookies.set(COOKIE_AUTH_TOKEN, token, cookieOptions);
  Cookies.set(COOKIE_AUTH_TOKEN_EXPIRES_AT, tokenExpiresAt, cookieOptions);
}

export function clearAuthCookies() {
  Cookies.remove(COOKIE_DOMAIN_TITLE);
  Cookies.remove(COOKIE_AUTH_TOKEN);
  Cookies.remove(COOKIE_AUTH_TOKEN_EXPIRES_AT);
  Cookies.remove(COOKIE_EMPLOYEE_DOMAIN);
  Cookies.remove(COOKIE_EMPLOYEE_NAME);
  Cookies.remove(COOKIE_EMPLOYEE_FLAG);
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("employeeVerified");
  }
}

function redirectToExpiredLogin() {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/signon") return;
  clearAuthCookies();
  window.location.href = "/signon?reason=session_expired";
}

axios.interceptors.response.use(
  (response) => {
    const data = response.data;
    const isBlob = typeof Blob !== "undefined" && data instanceof Blob;
    if (data && typeof data === "object" && !isBlob && (data as any).auth_token) {
      updateAuthTokenCookie((data as any).auth_token);
    }
    const headerToken = response.headers?.["x-auth-token"];
    const headerExpiresAt = response.headers?.["x-auth-token-expires-at"];
    const headerReissueExpiresAt = response.headers?.["x-auth-token-reissue-expires-at"];
    if (headerToken && headerExpiresAt) {
      updateAuthTokenCookie({
        token: headerToken,
        token_expires_at: headerExpiresAt,
        token_reissue_expires_at: headerReissueExpiresAt,
      });
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url ?? "");
    if (status === 401 && !requestUrl.includes("/personal/login")) {
      redirectToExpiredLogin();
    }
    return Promise.reject(error);
  }
);

// export function getColumnWidth(charCount: number, baseFontSize: number = 10): number {
//   return (charCount + 0.3) * baseFontSize*1.57;
// }
// export function getColumnWidth(charCount: number | string, baseFontSize: number = 10): number {
//   // --- 文字列の場合 ---
//   if (typeof charCount === "string") {
//     const trimmed = charCount.trim();
//     // --- 数値文字列として扱える場合（"2" "2.0" "2." ".5" など） ---
//     const num = Number(trimmed);
//     if (!isNaN(num)) {
//       return (num + 0.3) * baseFontSize * 1.57;
//     }
//     // --- CSS的な幅指定（vw / dvw） ---
//     const vwMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*vw$/i);
//     const dvwMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*dvw$/i);
//     if (vwMatch) {
//       const v = Number(vwMatch[1]) / 100;
//       return window.innerWidth * v;
//     }
//     if (dvwMatch) {
//       const v = Number(dvwMatch[1]) / 100;
//       return window.innerWidth * v;
//     }
//     console.warn("未対応の幅指定:", charCount);
//     return 0;
//   }

//   // --- 数値のとき（従来どおり） ---
//   return (charCount + 0.3) * baseFontSize * 1.57;
// }

export function getColumnWidth( charCount: number | string, baseFontSize: number = 10): number {
  // 文字数に足す余白係数
  const CHAR_PADDING = 1.2;
  // フォント幅補正係数
  const WIDTH_FACTOR = 1.61;

  // --- 文字列の場合 ---
  if (typeof charCount === "string") {
    const trimmed = charCount.trim();

    const num = Number(trimmed);
    if (!isNaN(num)) {
      return (num + CHAR_PADDING) * baseFontSize * WIDTH_FACTOR;
    }

    const vwMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*vw$/i);
    const dvwMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*dvw$/i);

    if (vwMatch || dvwMatch) {
      const v = Number((vwMatch ?? dvwMatch)![1]) / 100;
      return window.innerWidth * v;
    }

    console.warn("未対応の幅指定:", charCount);
    return 0;
  }

  // --- 数値のとき ---
  return (charCount + CHAR_PADDING) * baseFontSize * WIDTH_FACTOR;
}


export function getRowHeight(rowCount: number, baseFontSize: number = 10, bLabelMode: boolean = false): number {
  // return (rowCount + 0.7) * baseFontSize*1.94;
  return (rowCount + (bLabelMode ? 0.2 : 0.7)) * baseFontSize * 1.94;
}

export function firstKakko(text: string) {
    const match = text.match(/\((.*?)\)|（(.*?)）/);
    if (match) {
        return match[1] !== undefined ? match[1] : match[2];
    }
    return '';
}

export function removeKakko(text: string) {
    return text.replace(/\(.*?\)|（.*?）/g, '');
}



